// In-app Help & Support modal. Two tabs: Report Issue + FAQ.
//
// Layout rules (from spec):
//  • Desktop ≥ 768px  → centered modal, 480px wide, content-driven height
//                        (max-h 70vh, internal scroll)
//  • Mobile  < 768px  → bottom-sheet (slides up), full-width with side
//                        margins, max-h 85vh, rounded top corners only
//  • ESC + overlay click close
import { useEffect, useState } from "react";
import {
  X, Bug, Zap, CreditCard, Instagram, Lightbulb, UserCircle,
  HelpCircle, Loader2, CheckCircle2, ChevronDown, Search,
  ThumbsUp, ThumbsDown, Paperclip, ChevronRight,
} from "lucide-react";
import { api } from "../../App";
import { useSupport } from "../../context/SupportContext";

const ISSUE_TYPES = [
  { id: "bug",                    label: "Bug / Something broken",      icon: Bug },
  { id: "automation_not_working", label: "Automation not working",       icon: Zap },
  { id: "payment_issue",          label: "Payment issue",                icon: CreditCard },
  { id: "instagram_connection",   label: "Instagram connection issue",   icon: Instagram },
  { id: "feature_request",        label: "Feature request",              icon: Lightbulb },
  { id: "account_issue",          label: "Account issue",                icon: UserCircle },
  { id: "other",                  label: "Other",                        icon: HelpCircle },
];

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const StatusPill = ({ status }) => {
  const map = {
    open:        ["bg-amber-500/15 text-amber-600 dark:text-amber-400", "Open"],
    in_progress: ["bg-violet-500/15 text-violet-600 dark:text-violet-300", "In progress"],
    resolved:    ["bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", "Resolved"],
    closed:      ["bg-muted text-muted-foreground", "Closed"],
  }[status] || ["bg-muted text-muted-foreground", status];
  return (
    <span className={`inline-flex items-center px-1.5 h-[18px] rounded-full text-[10px] font-medium ${map[0]}`}>
      {map[1]}
    </span>
  );
};

export default function SupportModal() {
  const { isOpen, closeSupport, initialTab } = useSupport();
  const [tab, setTab] = useState("report");

  useEffect(() => {
    if (isOpen) setTab(initialTab || "report");
  }, [isOpen, initialTab]);

  // Lock body scroll while open (avoids content jiggle behind the
  // overlay). Cleaned up on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end md:items-center md:justify-center"
      data-testid="support-modal-overlay"
    >
      {/* Backdrop — captures outside clicks to close */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in"
        onClick={closeSupport}
        data-testid="support-modal-backdrop"
      />

      {/* Modal panel — centered on desktop, bottom-sheet on mobile */}
      <div
        className="
          relative bg-background border border-border/60 shadow-2xl
          w-full md:w-[480px]
          mx-0 md:mx-4
          rounded-t-2xl md:rounded-2xl
          max-h-[85vh] md:max-h-[70vh]
          flex flex-col
          animate-in slide-in-from-bottom md:zoom-in-95 duration-300
        "
        data-testid="support-modal"
      >
        {/* Header — compact 48px */}
        <div className="h-12 px-4 flex items-center justify-between border-b border-border/50 shrink-0">
          <h2 className="text-base font-semibold">Help &amp; Support</h2>
          <button
            type="button"
            onClick={closeSupport}
            data-testid="support-close-btn"
            className="p-1.5 rounded-lg hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs — compact 36px */}
        <div className="flex border-b border-border/50 shrink-0">
          <TabButton id="report" current={tab} onClick={setTab} testId="support-tab-report">
            <Bug className="w-3.5 h-3.5" /> Report Issue
          </TabButton>
          <TabButton id="faq" current={tab} onClick={setTab} testId="support-tab-faq">
            <HelpCircle className="w-3.5 h-3.5" /> FAQ
          </TabButton>
        </div>

        {/* Body — scrolls internally */}
        <div className="flex-1 overflow-y-auto">
          {tab === "report" ? <ReportIssueTab /> : <FaqTab onSwitchToReport={() => setTab("report")} />}
        </div>
      </div>
    </div>
  );
}

const TabButton = ({ id, current, onClick, children, testId }) => (
  <button
    type="button"
    onClick={() => onClick(id)}
    data-testid={testId}
    className={`flex-1 h-9 inline-flex items-center justify-center gap-1.5 text-[13px] font-medium transition border-b-2 ${
      current === id
        ? "border-primary text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

// ─── Tab 1: Report Issue ────────────────────────────────────────────
// Bug #5 (Apr 2026) — switched to inline-onBlur validation.
//   - Description: any text, min 20 chars (no punctuation rule).
//   - WhatsApp: 10-digit Indian OR `+` followed by 10-15 digits.
//   - Email: standard format.
//   - Issue type: required.
// Errors render inline as the user leaves each field; submit stays
// disabled until every field is valid.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const WHATSAPP_RE = /^(\d{10}|\+\d{10,15})$/;

function validateField(name, value) {
  switch (name) {
    case "issueType":
      return value ? "" : "Please pick an issue type";
    case "secondaryEmail":
      if (!value.trim()) return "This field is required";
      return EMAIL_RE.test(value.trim()) ? "" : "Enter a valid email";
    case "whatsapp": {
      if (!value.trim()) return "This field is required";
      const stripped = value.trim().replace(/[\s-]/g, "");
      return WHATSAPP_RE.test(stripped)
        ? ""
        : "Enter a valid WhatsApp number (e.g. +91 98765 43210)";
    }
    case "description":
      if (!value.trim()) return "This field is required";
      return value.trim().length >= 20
        ? "" : `Description needs at least 20 characters (currently ${value.trim().length})`;
    default:
      return "";
  }
}

function ReportIssueTab() {
  const [issueType, setIssueType] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});  // {field: message}
  const [touched, setTouched] = useState({});  // {field: true}
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [screenshotName, setScreenshotName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);

  // Pre-fill secondary email from logged-in user (best-effort).
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (data?.email && !secondaryEmail) setSecondaryEmail(data.email);
    }).catch(() => {});
    api.get("/support/my-tickets").then(({ data }) => {
      setHistory(data?.tickets || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whole-form validity computed from raw values (not from `errors`,
  // which only populates after blur — submit must still be disabled
  // for un-touched-but-empty fields).
  const valid = !validateField("issueType", issueType)
    && !validateField("secondaryEmail", secondaryEmail)
    && !validateField("whatsapp", whatsapp)
    && !validateField("description", description);

  const handleBlur = (name, value) => {
    setTouched((t) => ({ ...t, [name]: true }));
    setErrors((e) => ({ ...e, [name]: validateField(name, value) }));
  };
  // Re-run validation as user keeps typing AFTER they've already
  // touched the field — clears errors as soon as the input becomes
  // valid (so the form feels responsive).
  const handleChange = (name, value, setter) => {
    setter(value);
    if (touched[name]) {
      setErrors((e) => ({ ...e, [name]: validateField(name, value) }));
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Max 5MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScreenshotUrl(r.data?.url || null);
      setScreenshotName(file.name);
    } catch (e) {
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post("/support/ticket", {
        secondary_email: secondaryEmail.trim(),
        whatsapp_number: whatsapp.trim(),
        issue_type: issueType,
        description: description.trim(),
        screenshot_url: screenshotUrl,
      });
      setSuccess({
        ticket_number: r.data?.ticket_number || "TDL-???",
        secondary_email: secondaryEmail.trim(),
      });
      // Refresh history in the background
      api.get("/support/my-tickets").then(({ data }) => setHistory(data?.tickets || []))
        .catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setIssueType("");
    setDescription("");
    setScreenshotUrl(null);
    setScreenshotName(null);
  };

  if (success) {
    return (
      <div className="p-5 space-y-4 text-center">
        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
        <div>
          <h3 className="text-lg font-semibold">We got your report!</h3>
          <p className="text-[13px] text-muted-foreground mt-1">
            We'll reach out on WhatsApp within 24 hours.
          </p>
        </div>
        <div className="font-mono text-sm text-muted-foreground" data-testid="support-success-ticket">
          Ticket ID: {success.ticket_number}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Confirmation sent to {success.secondary_email}
        </p>
        <button
          type="button"
          onClick={reset}
          data-testid="support-submit-another"
          className="text-[13px] text-primary hover:underline"
        >
          Submit another issue
        </button>
        <HistoryAccordion history={history} show={showHistory} setShow={setShowHistory} />
      </div>
    );
  }

  const charCount = description.length;
  const descTrimLen = description.trim().length;
  return (
    <div className="p-4 space-y-3">
      <Field label="Issue type" error={touched.issueType ? errors.issueType : ""}>
        <select
          value={issueType}
          onChange={(e) => handleChange("issueType", e.target.value, setIssueType)}
          onBlur={(e) => handleBlur("issueType", e.target.value)}
          data-testid="support-issue-type"
          className={`w-full h-9 px-2 rounded-lg bg-secondary/50 border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
            touched.issueType && errors.issueType ? "border-red-500" : "border-border"
          }`}
        >
          <option value="">Select an issue type…</option>
          {ISSUE_TYPES.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </Field>

      <Field label="Reply email" error={touched.secondaryEmail ? errors.secondaryEmail : ""}>
        <input
          type="email"
          value={secondaryEmail}
          onChange={(e) => handleChange("secondaryEmail", e.target.value, setSecondaryEmail)}
          onBlur={(e) => handleBlur("secondaryEmail", e.target.value)}
          placeholder="your@email.com"
          data-testid="support-secondary-email"
          className={`w-full h-9 px-3 rounded-lg bg-secondary/50 border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
            touched.secondaryEmail && errors.secondaryEmail ? "border-red-500" : "border-border"
          }`}
        />
      </Field>

      <Field label="WhatsApp" error={touched.whatsapp ? errors.whatsapp : ""}>
        <input
          type="tel"
          value={whatsapp}
          onChange={(e) => handleChange("whatsapp", e.target.value, setWhatsapp)}
          onBlur={(e) => handleBlur("whatsapp", e.target.value)}
          placeholder="+91 98765 43210"
          data-testid="support-whatsapp"
          className={`w-full h-9 px-3 rounded-lg bg-secondary/50 border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
            touched.whatsapp && errors.whatsapp ? "border-red-500" : "border-border"
          }`}
        />
      </Field>

      <Field
        label="Describe the issue"
        trailing={
          <span className={
            descTrimLen > 0 && descTrimLen < 20 ? "text-red-500" : "text-muted-foreground"
          }>
            {charCount}/500
          </span>
        }
        error={touched.description ? errors.description : ""}
      >
        <textarea
          value={description.slice(0, 500)}
          onChange={(e) =>
            handleChange("description", e.target.value.slice(0, 500), setDescription)}
          onBlur={(e) => handleBlur("description", e.target.value)}
          placeholder="What happened? What did you expect?"
          rows={3}
          data-testid="support-description"
          className={`w-full px-3 py-2 rounded-lg bg-secondary/50 border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none ${
            touched.description && errors.description ? "border-red-500" : "border-border"
          }`}
        />
      </Field>

      <div>
        <input
          id="support-screenshot"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid="support-screenshot-input"
        />
        {!screenshotUrl ? (
          <label
            htmlFor="support-screenshot"
            className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground cursor-pointer"
            data-testid="support-screenshot-label"
          >
            <Paperclip className="w-3.5 h-3.5" />
            {uploading ? "Uploading…" : "Attach screenshot (optional)"}
          </label>
        ) : (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Paperclip className="w-3.5 h-3.5" />
            <span className="truncate max-w-[280px]" data-testid="support-screenshot-name">{screenshotName}</span>
            <button
              type="button"
              onClick={() => { setScreenshotUrl(null); setScreenshotName(null); }}
              className="text-muted-foreground/60 hover:text-foreground"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={!valid || submitting}
        title={!valid ? "Please fill all required fields" : ""}
        data-testid="support-submit-btn"
        className="w-full h-10 rounded-xl bg-primary text-primary-foreground font-medium text-sm flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-[0.99]"
      >
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitting ? "Submitting…" : "Submit Report"}
      </button>

      <HistoryAccordion history={history} show={showHistory} setShow={setShowHistory} />
    </div>
  );
}

const Field = ({ label, trailing, error, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <label className="text-[12px] font-medium text-muted-foreground">{label}</label>
      {trailing && (typeof trailing === "string"
        ? <span className="text-[11px] text-muted-foreground">{trailing}</span>
        : trailing)}
    </div>
    {children}
    {error && (
      <p
        className="mt-1 text-[11px] text-red-500"
        data-testid={`field-error-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {error}
      </p>
    )}
  </div>
);

const HistoryAccordion = ({ history, show, setShow }) => {
  const top = (history || []).slice(0, 3);
  if (top.length === 0) return null;
  return (
    <div className="border-t border-border/50 mt-3 pt-3" data-testid="support-history-section">
      <button
        type="button"
        onClick={() => setShow(!show)}
        data-testid="support-history-toggle"
        className="w-full flex items-center justify-between text-[12px] font-medium text-muted-foreground hover:text-foreground"
      >
        <span>My previous tickets ({history.length})</span>
        <ChevronDown className={`w-4 h-4 transition ${show ? "rotate-180" : ""}`} />
      </button>
      {show && (
        <ul className="mt-2 space-y-1.5">
          {top.map((t) => (
            <li
              key={t.id}
              className="flex items-center gap-2 text-[12px] text-muted-foreground"
              data-testid={`support-history-item-${t.id}`}
            >
              <span className="font-mono">{t.ticket_number_display}</span>
              <span>·</span>
              <span className="capitalize">{(t.issue_type || "").replace(/_/g, " ")}</span>
              <span>·</span>
              <StatusPill status={t.status} />
              <span className="ml-auto">{timeAgo(t.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Tab 2: FAQ ─────────────────────────────────────────────────────
const ALL_CATEGORIES = ["All", "Getting Started", "Automations", "Analytics", "Billing & Plans", "Account"];

function FaqTab({ onSwitchToReport }) {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("All");
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState({});

  // Debounce search 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch on filter change
  useEffect(() => {
    const params = {};
    if (debounced) params.search = debounced;
    if (category && category !== "All") params.category = category;
    api.get("/support/faq", { params }).then(({ data }) => {
      setArticles(data?.articles || []);
    }).catch(() => {});
  }, [debounced, category]);

  const toggle = (id) => {
    setOpen((s) => ({ ...s, [id]: !s[id] }));
    if (!open[id]) {
      // Increment view_count once on first expand.
      api.post(`/support/faq/${id}/view`).catch(() => {});
    }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help articles..."
          data-testid="support-faq-search"
          className="w-full h-9 pl-8 pr-3 rounded-lg bg-secondary/50 border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Category pills — horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-testid="support-faq-categories">
        {ALL_CATEGORIES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCategory(c)}
            data-testid={`support-faq-category-${c.replace(/\s|&/g, "")}`}
            className={`shrink-0 h-7 px-3 rounded-full text-[12px] font-medium transition border ${
              category === c
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-transparent text-muted-foreground border-border/60 hover:text-foreground"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Articles */}
      {articles.length === 0 ? (
        <div className="text-center py-10" data-testid="support-faq-empty">
          <HelpCircle className="w-8 h-8 mx-auto text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground mt-2">No results for "{debounced || category}"</p>
          <button
            type="button"
            onClick={onSwitchToReport}
            className="text-[13px] text-primary hover:underline mt-2"
            data-testid="support-faq-empty-cta"
          >
            Report an issue →
          </button>
        </div>
      ) : (
        <ul className="space-y-1">
          {articles.map((a) => (
            <FaqRow
              key={a.id}
              article={a}
              isOpen={!!open[a.id]}
              onToggle={() => toggle(a.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FaqRow({ article, isOpen, onToggle }) {
  const [voted, setVoted] = useState(false);

  const onVote = async (helpful) => {
    if (voted) return;
    setVoted(true);
    try {
      await api.post(`/support/faq/${article.id}/helpful`, { helpful });
    } catch (e) { /* swallow */ }
  };

  return (
    <li className="border-b border-border/40 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`support-faq-row-${article.id}`}
        className="w-full min-h-[40px] py-2 flex items-center gap-2 text-left text-[14px] hover:text-primary transition"
      >
        <span className="flex-1">{article.question}</span>
        <ChevronRight className={`w-4 h-4 shrink-0 text-muted-foreground transition ${isOpen ? "rotate-90" : ""}`} />
      </button>
      {isOpen && (
        <div className="pb-3 px-1 space-y-2">
          <div
            className="text-[13px] text-muted-foreground leading-relaxed whitespace-pre-line"
            data-testid={`support-faq-answer-${article.id}`}
          >
            {article.answer}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[12px] text-muted-foreground">Was this helpful?</span>
            {voted ? (
              <span className="text-[12px] text-emerald-500" data-testid={`support-faq-voted-${article.id}`}>
                Thanks!
              </span>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onVote(true)}
                  data-testid={`support-faq-helpful-${article.id}`}
                  className="w-7 h-7 rounded-md hover:bg-secondary/60 inline-flex items-center justify-center text-muted-foreground hover:text-emerald-500 transition"
                >
                  <ThumbsUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => onVote(false)}
                  data-testid={`support-faq-not-helpful-${article.id}`}
                  className="w-7 h-7 rounded-md hover:bg-secondary/60 inline-flex items-center justify-center text-muted-foreground hover:text-rose-500 transition"
                >
                  <ThumbsDown className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
